/**
 * Sacred Geometry Visualizer - Flower of Life, Metatron's Cube, Sri Yantra, etc.
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class SacredVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;
  private rotationVelocity = 0;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'sacred',
    name: 'Sacred Geometry',
    description: 'Flower of Life, Metatron\'s Cube, Sri Yantra patterns',
    category: 'artistic',
    defaultZoom: 1.0,
    supports3D: false,
    supportsAutoRotation: true,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): number | void {
    const {
      ctx, width, height, activeWaves, speed, time, deltaTime,
      zoom, pan, isAutoRotating, autoRotRef = 0,
      smoothedMusicData, isUploadedAudioPlaying, uploadedAudioData
    } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Deep mystical background
    const bgGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height));
    bgGradient.addColorStop(0, '#0a0520');
    bgGradient.addColorStop(0.5, '#050212');
    bgGradient.addColorStop(1, '#000000');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, width, height);
    
    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }
    
    // Auto Rotation Logic (with music-reactive inertia)
    let newAutoRot = autoRotRef;
    if (isAutoRotating) {
      const rotationSpeed = 0.1;
      
      if (smoothedMusicData && isUploadedAudioPlaying) {
        // Treble-influenced rotation for sacred geometry
        const trebleInfluence = (smoothedMusicData.trebleEnergy / 300) * 0.15;
        const targetVelocity = rotationSpeed + trebleInfluence;
        this.rotationVelocity = this.rotationVelocity * 0.9 + targetVelocity * 0.1;
        newAutoRot += deltaTime * this.rotationVelocity;
      } else {
        newAutoRot += deltaTime * rotationSpeed;
        this.rotationVelocity = rotationSpeed;
      }
    }
    
    const rotation = newAutoRot + (pan.x * 0.002);
    const scale = Math.min(width, height) * 0.35 * zoom;
    
    // Calculate total energy and frequency characteristics
    const totalEnergy = smoothedMusicData 
      ? smoothedMusicData.totalEnergy / 10
      : displayWaves.reduce((sum, w) => sum + w.amp, 0);
    const avgFreq = smoothedMusicData
      ? (smoothedMusicData.bassEnergy * 0.5 + smoothedMusicData.midEnergy * 2 + smoothedMusicData.trebleEnergy * 5) / (smoothedMusicData.totalEnergy || 1)
      : (displayWaves.length > 0 ? displayWaves.reduce((sum, w) => sum + (w.freq * w.amp), 0) / totalEnergy : 1);
    
    // Helper: Draw circle with pulsation
    const drawCircle = (x: number, y: number, radius: number, color: string, pulsePhase: number, lineWidth: number = 2) => {
      const pulse = 1 + Math.sin(time * avgFreq + pulsePhase) * 0.1;
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.arc(x, y, radius * pulse, 0, Math.PI * 2);
      ctx.stroke();
    };
    
    // Helper: Get wave color with alpha
    const getWaveColor = (waveIndex: number, alpha: number = 0.6) => {
      if (displayWaves.length === 0) return `rgba(255, 215, 0, ${alpha})`;
      const wave = displayWaves[waveIndex % displayWaves.length];
      const r = parseInt(wave.color.slice(1, 3), 16);
      const g = parseInt(wave.color.slice(3, 5), 16);
      const b = parseInt(wave.color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    // Save context for transformations
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    
    // === FLOWER OF LIFE === (Foundation pattern)
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(255, 215, 0, 0.5)';
    
    const flowerRadius = scale * 0.5;
    
    // Create 6 circles around center (first ring)
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x = Math.cos(angle) * flowerRadius;
      const y = Math.sin(angle) * flowerRadius;
      
      const pulsePhase = i * Math.PI / 3;
      drawCircle(x, y, flowerRadius, getWaveColor(i, 0.4), pulsePhase, 1.5);
    }
    
    // Center circle
    drawCircle(0, 0, flowerRadius, getWaveColor(0, 0.5), 0, 2);
    
    // Second ring (outer flower petals)
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x = Math.cos(angle) * flowerRadius * 2;
      const y = Math.sin(angle) * flowerRadius * 2;
      const pulsePhase = i * Math.PI / 3 + Math.PI;
      drawCircle(x, y, flowerRadius, getWaveColor(i, 0.25), pulsePhase, 1);
    }
    
    // === VESICA PISCIS === (Intersection of two circles)
    ctx.shadowBlur = 20;
    ctx.shadowColor = 'rgba(138, 43, 226, 0.6)';
    
    const vesicaRadius = scale * 0.4;
    const vesicaOffset = vesicaRadius * 0.5;
    
    ctx.strokeStyle = getWaveColor(0, 0.6);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(-vesicaOffset, 0, vesicaRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.strokeStyle = getWaveColor(1, 0.6);
    ctx.beginPath();
    ctx.arc(vesicaOffset, 0, vesicaRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    // Fill the vesica piscis (sacred almond shape)
    ctx.fillStyle = getWaveColor(2, 0.1);
    ctx.beginPath();
    ctx.arc(-vesicaOffset, 0, vesicaRadius, -Math.asin(vesicaOffset/vesicaRadius), Math.asin(vesicaOffset/vesicaRadius));
    ctx.arc(vesicaOffset, 0, vesicaRadius, Math.PI - Math.asin(vesicaOffset/vesicaRadius), Math.PI + Math.asin(vesicaOffset/vesicaRadius));
    ctx.closePath();
    ctx.fill();
    
    // === METATRON'S CUBE === (13 circles connected)
    ctx.shadowBlur = 10;
    const metatronRadius = scale * 0.2;
    const metatronPositions: {x: number, y: number}[] = [{x: 0, y: 0}];
    
    // Inner hexagon
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      metatronPositions.push({
        x: Math.cos(angle) * metatronRadius,
        y: Math.sin(angle) * metatronRadius
      });
    }
    
    // Outer hexagon
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + Math.PI / 6;
      metatronPositions.push({
        x: Math.cos(angle) * metatronRadius * 1.8,
        y: Math.sin(angle) * metatronRadius * 1.8
      });
    }
    
    // Draw Metatron's connections
    ctx.strokeStyle = getWaveColor(3, 0.3);
    ctx.lineWidth = 1;
    for (let i = 0; i < metatronPositions.length; i++) {
      for (let j = i + 1; j < metatronPositions.length; j++) {
        const p1 = metatronPositions[i];
        const p2 = metatronPositions[j];
        const dist = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
        
        if (dist < metatronRadius * 2.5) {
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      }
    }
    
    // Draw Metatron's circles
    metatronPositions.forEach((pos, i) => {
      const circleRadius = metatronRadius * 0.15;
      const pulsePhase = i * Math.PI / 6;
      const pulse = 1 + Math.sin(time * avgFreq * 2 + pulsePhase) * 0.2;
      
      ctx.shadowColor = getWaveColor(i, 0.8);
      ctx.fillStyle = getWaveColor(i, 0.4);
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, circleRadius * pulse, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // === GOLDEN RATIO SPIRAL === (Fibonacci spiral)
    ctx.shadowBlur = 12;
    ctx.shadowColor = 'rgba(255, 215, 0, 0.6)';
    
    displayWaves.forEach((wave, waveIndex) => {
      const effectiveFreq = wave.freq * speed;
      const phaseOffset = (wave.phase * Math.PI) / 180;
      const spiralScale = scale * 0.6 * (wave.amp / 100);
      
      ctx.strokeStyle = getWaveColor(waveIndex, 0.5);
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      const goldenRatio = 1.618033988749;
      const numPoints = 120;
      
      for (let i = 0; i < numPoints; i++) {
        const t = i / numPoints;
        const angle = t * Math.PI * 6 + (time * effectiveFreq * 0.5) + phaseOffset;
        const radius = spiralScale * Math.pow(goldenRatio, t * 4 - 2);
        
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    });
    
    // === SRI YANTRA === (Sacred Hindu/Buddhist mandala)
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(255, 20, 147, 0.5)';
    
    const yantaRadius = scale * 0.8;
    
    // Draw 9 interlocking triangles (4 pointing up, 5 pointing down)
    const triangleConfigs = [
      // Upward triangles (masculine/Shiva)
      { scale: 0.9, rotation: 0, color: getWaveColor(0, 0.35), pointing: 'up' as const },
      { scale: 0.7, rotation: Math.PI / 9, color: getWaveColor(1, 0.35), pointing: 'up' as const },
      { scale: 0.5, rotation: Math.PI / 6, color: getWaveColor(2, 0.35), pointing: 'up' as const },
      { scale: 0.3, rotation: Math.PI / 4, color: getWaveColor(3, 0.35), pointing: 'up' as const },
      // Downward triangles (feminine/Shakti)
      { scale: 0.85, rotation: Math.PI, color: getWaveColor(4, 0.35), pointing: 'down' as const },
      { scale: 0.65, rotation: Math.PI + Math.PI / 9, color: getWaveColor(5, 0.35), pointing: 'down' as const },
      { scale: 0.45, rotation: Math.PI + Math.PI / 6, color: getWaveColor(6, 0.35), pointing: 'down' as const },
      { scale: 0.25, rotation: Math.PI + Math.PI / 4, color: getWaveColor(7, 0.35), pointing: 'down' as const },
      { scale: 0.15, rotation: Math.PI + Math.PI / 3, color: getWaveColor(0, 0.4), pointing: 'down' as const }
    ];
    
    triangleConfigs.forEach((config, i) => {
      const triSize = yantaRadius * config.scale;
      const pulse = 1 + Math.sin(time * avgFreq + i * Math.PI / 4) * 0.05;
      const rotOffset = config.pointing === 'up' ? 0 : Math.PI;
      
      ctx.strokeStyle = config.color;
      ctx.fillStyle = config.color.replace(/[\d.]+\)$/g, '0.08)');
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      
      for (let j = 0; j < 3; j++) {
        const angle = (j * 2 * Math.PI / 3) + config.rotation + rotOffset;
        const x = Math.cos(angle) * triSize * pulse;
        const y = Math.sin(angle) * triSize * pulse;
        if (j === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });
    
    // === TORUS ENERGY FIELD === (3D projection)
    ctx.shadowBlur = 20;
    ctx.shadowColor = 'rgba(0, 255, 255, 0.5)';
    
    const torusR = scale * 0.45; // Major radius
    const torusr = scale * 0.15; // Minor radius
    const torusSegments = 40;
    const torusTubes = 20;
    
    // Simple orthographic projection
    const camAngle = time * avgFreq * 0.3;
    
    for (let i = 0; i < torusSegments; i++) {
      const u = (i / torusSegments) * Math.PI * 2;
      
      for (let j = 0; j < torusTubes; j += 2) {
        const v = (j / torusTubes) * Math.PI * 2;
        
        // Parametric torus equations
        const x = (torusR + torusr * Math.cos(v)) * Math.cos(u);
        const y = (torusR + torusr * Math.cos(v)) * Math.sin(u);
        const z = torusr * Math.sin(v);
        
        // Rotate around Y axis
        const rotX = x * Math.cos(camAngle) - z * Math.sin(camAngle);
        const rotZ = x * Math.sin(camAngle) + z * Math.cos(camAngle);
        
        // Project to 2D (orthographic)
        const screenX = rotX;
        const screenY = y;
        
        // Depth-based coloring
        const depth = (rotZ + torusR + torusr) / ((torusR + torusr) * 2);
        const alpha = 0.15 + depth * 0.25;
        
        ctx.fillStyle = getWaveColor(Math.floor(i / 5) % displayWaves.length, alpha);
        ctx.beginPath();
        ctx.arc(screenX, screenY, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // === CENTRAL BINDU === (Sacred point/seed)
    ctx.shadowBlur = 30;
    ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
    
    const binduGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 12);
    binduGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    binduGradient.addColorStop(0.5, 'rgba(255, 215, 0, 0.8)');
    binduGradient.addColorStop(1, 'rgba(138, 43, 226, 0.2)');
    
    const binduPulse = 1 + Math.sin(time * avgFreq * 3) * 0.3;
    ctx.fillStyle = binduGradient;
    ctx.beginPath();
    ctx.arc(0, 0, 8 * binduPulse, 0, Math.PI * 2);
    ctx.fill();
    
    // === PLATONIC SOLIDS OVERLAY === (Wireframe)
    // Dodecahedron vertices projection
    if (totalEnergy > 50) {
      ctx.shadowBlur = 8;
      ctx.strokeStyle = getWaveColor(0, 0.2);
      ctx.lineWidth = 0.5;
      
      const phi = (1 + Math.sqrt(5)) / 2; // Golden ratio
      const dodecaR = scale * 0.6;
      
      // Simplified dodecahedron vertices
      const vertices = [
        [1, 1, 1], [-1, 1, 1], [1, -1, 1], [1, 1, -1],
        [0, phi, 1/phi], [0, -phi, 1/phi], [0, phi, -1/phi], [0, -phi, -1/phi],
        [1/phi, 0, phi], [-1/phi, 0, phi], [1/phi, 0, -phi], [-1/phi, 0, -phi],
        [phi, 1/phi, 0], [-phi, 1/phi, 0], [phi, -1/phi, 0], [-phi, -1/phi, 0]
      ];
      
      const rotY = time * avgFreq * 0.2;
      const rotX = time * avgFreq * 0.15;
      
      const projectedVerts = vertices.map(([x, y, z]) => {
        // Rotate Y
        const rx = x * Math.cos(rotY) - z * Math.sin(rotY);
        let rz = x * Math.sin(rotY) + z * Math.cos(rotY);
        // Rotate X
        const ry = y * Math.cos(rotX) - rz * Math.sin(rotX);
        rz = y * Math.sin(rotX) + rz * Math.cos(rotX);
        
        return { x: rx * dodecaR * 0.3, y: ry * dodecaR * 0.3, z: rz };
      });
      
      // Draw edges between close vertices
      for (let i = 0; i < projectedVerts.length; i++) {
        for (let j = i + 1; j < projectedVerts.length; j++) {
          const v1 = projectedVerts[i];
          const v2 = projectedVerts[j];
          const dist = Math.sqrt((v2.x - v1.x) ** 2 + (v2.y - v1.y) ** 2);
          
          if (dist < dodecaR * 0.5) {
            ctx.beginPath();
            ctx.moveTo(v1.x, v1.y);
            ctx.lineTo(v2.x, v2.y);
            ctx.stroke();
          }
        }
      }
    }
    
    ctx.restore();
    ctx.shadowBlur = 0;
    
    // === SACRED MANTRAS === (Visual frequency bands)
    const numBands = Math.min(displayWaves.length, 8);
    const bandRadius = scale * 1.3;
    
    displayWaves.slice(0, numBands).forEach((wave, i) => {
      const angle = (i / numBands) * Math.PI * 2 - Math.PI / 2;
      const effectiveFreq = wave.freq * speed;
      const barHeight = (wave.amp / 100) * scale * 0.3;
      const pulse = 1 + Math.sin(time * effectiveFreq * 2) * 0.2;
      
      const x1 = cx + Math.cos(angle) * bandRadius;
      const y1 = cy + Math.sin(angle) * bandRadius;
      const x2 = cx + Math.cos(angle) * (bandRadius + barHeight * pulse);
      const y2 = cy + Math.sin(angle) * (bandRadius + barHeight * pulse);
      
      const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
      gradient.addColorStop(0, getWaveColor(i, 0.2));
      gradient.addColorStop(1, getWaveColor(i, 0.8));
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 4;
      ctx.shadowBlur = 10;
      ctx.shadowColor = getWaveColor(i, 0.6);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });
    
    ctx.shadowBlur = 0;
    return newAutoRot;
  }
}
